/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {isSelectedDeviceSCI} from '@ion/dls/src/editors/DeviceEditorUtils';
import {
  deviceSwapSelectedSelector,
  deviceSwapShowConfirmDialogSelector,
  deviceSwapShowDialogSelector
} from './selector';
import {
  swapAdminDevice,
  swapAdminDeviceConfirm
} from './state';

export const useSwapDetails = () => {
  const dispatch              = useDispatch();
  const showConfirmSwapDevice = useSelector(deviceSwapShowConfirmDialogSelector);
  const showSwapDevice        = useSelector(deviceSwapShowDialogSelector);
  const selectedSwapDevice    = useSelector(deviceSwapSelectedSelector);
  const isGroupAssociated     = !!selectedSwapDevice?.groups?.length;
  const isAssetAssociated     = !!selectedSwapDevice?.asset?.href;
  const isSCIDevice           = isSelectedDeviceSCI(selectedSwapDevice);
  const deviceName            = selectedSwapDevice?.name;

  const swapColumns = ['account', 'deviceConfiguration', 'esn', 'serialNumber',
    'iccid', 'imei', 'imei2', 'isdn', 'meid', 'name', 'shipDate', 'status', 'type', 'version'];

  const onSwapDismiss = useCallback(() => {
    dispatch(swapAdminDeviceConfirm(null, false));
  }, []);

  const onSwapAction = useCallback(() => {
    dispatch(swapAdminDevice(selectedSwapDevice));
  }, [selectedSwapDevice]);

  const swapAssociatedTitle = function() {
    if (isAssetAssociated || isSCIDevice) {
      return translate('admin-devices:swap.unableToSwap');
    }
    return `${translate('common:editors.shared.swap', {entityLabel: translate('common:entityLabels.device')})}?`;
  };

  const swapAssociatedMessage = function() {
    if (isSCIDevice) {
      return `${translate('admin-devices:swap.sciDeviceMessage', {device: deviceName})}`;
    } else if (isAssetAssociated) {
      return `${translate('admin-devices:swap.assetAssociatedMessage', {device: deviceName})}`;
    }
    return `${translate('admin-devices:swap.groupAssociatedMessage', {device: deviceName})}`;
  };

  const getDismissLabel = function() {
    if (isAssetAssociated || isSCIDevice) {
      return translate('common:ok');
    }
    return translate('common:groupChooser.neverMind');
  };

  return {
    showConfirmSwapDevice,
    showSwapDevice,
    selectedSwapDevice,
    swapColumns,
    isAssetAssociated,
    isGroupAssociated,
    isSCIDevice,
    swapAssociatedTitle,
    swapAssociatedMessage,
    getDismissLabel,
    onSwapDismiss,
    onSwapAction
  };
};
